"use client";

import { ModalContent, type ModalRenderProps, openModal } from "@decibeltrade/web-core/modal";
import { usePathname, useRouter } from "next/navigation";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import type { WinnerForUI } from "../data/types";
import { useClaimBannerDismissal } from "../hooks/use-claim-banner-dismissal";
import {
  CLAIM_FLOW_SECTION_ID,
  scrollToClaimFlow,
  useClaimFlowStore,
} from "../hooks/use-claim-flow-store";
import { useCardvaultClaimableWins } from "../hooks/use-claimable-wins";
import { cv } from "../tokens";
import { CardVaultButton } from "./cardvault-button";

const CLAIM_MODAL_ID = "cardvault-claim-modal";
const CARDVAULT_PATH = "/card-vault";

/**
 * Globally-mounted claim banner. Renders on every page of the app when the
 * connected wallet has an unclaimed win. Dismissible (in-memory only — comes
 * back on refresh). Auto-hides permanently once the claim POST succeeds and
 * the week joins the claimed list in Firestore.
 *
 * Mount this at the app layout level; do not render from inside a cardvault
 * page (would double-render).
 */
export function ClaimBanner() {
  const { claimableWins } = useCardvaultClaimableWins();
  const { dismissed, dismiss } = useClaimBannerDismissal();

  const win = claimableWins[0];
  if (!win || dismissed) return null;

  return (
    <div data-testid="cv-claim-banner" style={styles.banner}>
      <div style={styles.bannerInner}>
        <span style={styles.bannerText}>
          You won <strong>{win.cardName}</strong> in Week {win.week}!
        </span>
        <div style={styles.bannerActions}>
          <CardVaultButton
            data-testid="cv-claim-now-btn"
            onClick={() =>
              openModal({
                id: CLAIM_MODAL_ID,
                renderContent: (props) => <ClaimModal {...props} win={win} />,
              })
            }
          >
            CLAIM NOW
          </CardVaultButton>
          <button
            data-testid="cv-claim-banner-dismiss"
            aria-label="Dismiss"
            onClick={dismiss}
            style={styles.dismissBtn}
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}

function ClaimModal({ close, win }: ModalRenderProps & { win: WinnerForUI }) {
  const setSelectedWeek = useClaimFlowStore((s) => s.setSelectedWeek);
  const router = useRouter();
  const pathname = usePathname();

  const handleContinue = () => {
    setSelectedWeek(win.week);
    close();
    if (pathname === CARDVAULT_PATH) {
      // Already on the cardvault page — scroll after modal unmounts.
      setTimeout(scrollToClaimFlow, 0);
    } else {
      // Route to cardvault with the wizard as the hash target so Next.js
      // scrolls after the page mounts.
      router.push(`${CARDVAULT_PATH}#${CLAIM_FLOW_SECTION_ID}`);
    }
  };

  return (
    <ModalContent
      className={css({
        w: "[440px]",
        p: "24",
        bg: "background.canvas",
      })}
    >
      <div className={stack({ gap: "20" })}>
        <div className={css({ display: "flex", alignItems: "center", gap: "16" })}>
          {win.cardImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={win.cardImage}
              alt={win.cardName}
              className={css({ w: "[80px]", h: "auto", borderRadius: "200" })}
            />
          )}
          <div>
            <p className={css({ textStyle: "highlight.md", color: "text.primary" })}>
              {win.cardName}
            </p>
            <p className={css({ textStyle: "body.sm", color: "text.tertiary" })}>
              {win.grade} &middot; Week {win.week}
            </p>
          </div>
        </div>

        <h3 className={css({ textStyle: "highlight.lg", color: "text.primary" })}>
          You won this card!
        </h3>
        <p className={css({ textStyle: "body.md", color: "text.tertiary" })}>
          Continue to the claim flow to verify your wallet and tell us where to send it.
        </p>

        <CardVaultButton data-testid="cv-modal-continue-btn" onClick={handleContinue}>
          CONTINUE TO CLAIM
        </CardVaultButton>
        <button
          onClick={close}
          style={{
            background: "none",
            border: "none",
            color: cv.grey,
            cursor: "pointer",
            fontSize: "0.875rem",
            fontFamily: "inherit",
          }}
        >
          Cancel
        </button>
      </div>
    </ModalContent>
  );
}

const styles = {
  banner: {
    position: "fixed" as const,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 50,
    background: cv.yellow,
    padding: "12px 0",
  },
  bannerInner: {
    width: "90%",
    maxWidth: 1400,
    margin: "0 auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1rem",
  },
  bannerActions: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
  },
  bannerText: {
    color: cv.black,
    fontSize: "0.875rem",
  },
  dismissBtn: {
    background: "transparent",
    border: "none",
    color: cv.black,
    fontSize: "1.5rem",
    lineHeight: 1,
    cursor: "pointer",
    padding: "0 0.5rem",
    fontFamily: "inherit",
  },
};
